import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddTaskOutlinedIcon from '@mui/icons-material/AddTaskOutlined';
import DoNotDisturbAltOutlinedIcon from '@mui/icons-material/DoNotDisturbAltOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { collection, doc, getDocs, getFirestore, limit, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import AddLeadDrawer from '../components/AddLeadDrawer';
import LeadDetailDrawer from '../components/LeadDetailDrawer';

type SubmissionStatus = 'pending' | 'existing' | 'converted' | 'ignored';
type MetaSubmission = {
  id: string;
  status: SubmissionStatus;
  leadgenId?: string;
  formId?: string;
  pageId?: string;
  suggestedProjectId?: string | null;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  answers?: Record<string, string | string[]>;
  metaCreatedAt?: string | null;
  receivedAt?: any;
  convertedLeadId?: string | null;
  existingLeadId?: string | null;
  linkedExistingLead?: boolean;
};

type ExistingLead = {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  status: string;
  closedAt?: any;
  isMerged?: boolean;
  archived?: boolean;
  updatedAt?: any;
  createdAt?: any;
};

type FormOption = {
  id: string;
  count: number;
  latestAt: Date | null;
};

type QuestionInsight = {
  key: string;
  label: string;
  responseCount: number;
  answers: Array<{ value: string; count: number }>;
};

const GOLD = '#9d6f32';
// Google Sheet lead import was paused on this date. Older Meta submissions
// remain stored for audit, but are not part of the manual Meta Leads workflow.
const META_LEADS_CUTOVER = new Date('2026-09-20T00:00:00+05:30');

function asDate(value: any): Date | null {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function displayDate(submission: MetaSubmission): string {
  const date = asDate(submission.metaCreatedAt) || asDate(submission.receivedAt);
  return date ? date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'Receiving...';
}

function isAfterMetaLeadsCutover(submission: MetaSubmission): boolean {
  const submittedAt = asDate(submission.metaCreatedAt) || asDate(submission.receivedAt);
  return Boolean(submittedAt && submittedAt >= META_LEADS_CUTOVER);
}

function labelForAnswer(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function shouldShowInAccordion(key: string): boolean {
  const normalized = key.trim().toLowerCase().replace(/[\s-]+/g, '_');
  return !['inbox_url', 'full_name', 'name', 'phone', 'phone_number', 'email', 'email_address'].includes(normalized);
}

function phone10(value?: string | null): string {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : '';
}

function notesFromAnswers(submission: MetaSubmission): string[] {
  const answers = Object.entries(submission.answers || {})
    .filter(([key]) => shouldShowInAccordion(key))
    .map(([key, value]) => {
      const text = Array.isArray(value) ? value.join(', ') : String(value || '');
      return text.trim() ? `${labelForAnswer(key)}: ${text.trim()}` : '';
    })
    .filter(Boolean);
  return [
    ...answers,
  ];
}

function inputDate(value: Date | null): string {
  if (!value) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function MetaLeadInsights({ submissions }: { submissions: MetaSubmission[] }) {
  const forms = useMemo<FormOption[]>(() => {
    const grouped = new Map<string, FormOption>();
    submissions.forEach((submission) => {
      const id = submission.formId || 'unknown';
      const receivedAt = asDate(submission.metaCreatedAt) || asDate(submission.receivedAt);
      const current = grouped.get(id);
      grouped.set(id, {
        id,
        count: (current?.count || 0) + 1,
        latestAt: !current?.latestAt || (receivedAt && receivedAt > current.latestAt)
          ? receivedAt : current.latestAt,
      });
    });
    return Array.from(grouped.values()).sort((a, b) =>
      (b.latestAt?.getTime() || 0) - (a.latestAt?.getTime() || 0)
    );
  }, [submissions]);
  const [formId, setFormId] = useState('');
  const [fromDate, setFromDate] = useState('2026-09-20');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    if (!formId && forms[0]) setFormId(forms[0].id);
  }, [formId, forms]);

  const selectedSubmissions = useMemo(() => submissions.filter((submission) => {
    if (!formId || submission.formId !== formId) return false;
    const submittedAt = asDate(submission.metaCreatedAt) || asDate(submission.receivedAt);
    if (!submittedAt) return false;
    const day = inputDate(submittedAt);
    return (!fromDate || day >= fromDate) && (!toDate || day <= toDate);
  }), [formId, fromDate, submissions, toDate]);

  const questions = useMemo<QuestionInsight[]>(() => {
    const grouped = new Map<string, QuestionInsight & { values: Map<string, number> }>();
    selectedSubmissions.forEach((submission) => {
      Object.entries(submission.answers || {})
        .filter(([key]) => shouldShowInAccordion(key))
        .forEach(([key, rawValue]) => {
          const values = (Array.isArray(rawValue) ? rawValue : [rawValue])
            .map((value) => String(value || '').trim())
            .filter(Boolean);
          if (!values.length) return;
          const current = grouped.get(key) || {
            key,
            label: labelForAnswer(key),
            responseCount: 0,
            answers: [],
            values: new Map<string, number>(),
          };
          current.responseCount += 1;
          values.forEach((value) => current.values.set(value, (current.values.get(value) || 0) + 1));
          grouped.set(key, current);
        });
    });
    return Array.from(grouped.values())
      .map(({ values, ...question }) => ({
        ...question,
        answers: Array.from(values.entries())
          .map(([value, count]) => ({ value, count }))
          .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value)),
      }))
      .sort((a, b) => b.responseCount - a.responseCount || a.label.localeCompare(b.label));
  }, [selectedSubmissions]);

  const responseTotal = questions.reduce((sum, question) => sum + question.responseCount, 0);
  return <Stack spacing={2}>
    <Paper variant="outlined" sx={{ p: 2, borderColor: '#e5ddd1', bgcolor: '#fffdf9' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'center' }}>
        <TextField select label="Meta form" value={formId} onChange={(event) => setFormId(event.target.value)} size="small" sx={{ minWidth: { md: 340 } }}>
          {forms.map((form) => <MenuItem key={form.id} value={form.id}>{`Form ${form.id} (${form.count})`}</MenuItem>)}
        </TextField>
        <TextField label="From" type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} size="small" InputLabelProps={{ shrink: true }} />
        <TextField label="To" type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} size="small" InputLabelProps={{ shrink: true }} />
        <Box sx={{ flex: 1 }} />
        <Chip label={`${selectedSubmissions.length} submissions`} color="primary" variant="outlined" />
        <Chip label={`${questions.length} questions`} variant="outlined" />
      </Stack>
    </Paper>

    {!forms.length && <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderColor: '#e5ddd1' }}>
      <Typography variant="h6">No Meta form responses yet</Typography>
      <Typography color="text.secondary">Insights appear after a Meta form receives responses.</Typography>
    </Paper>}
    {forms.length > 0 && selectedSubmissions.length === 0 && <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderColor: '#e5ddd1' }}>
      <Typography variant="h6">No responses in this date range</Typography>
      <Typography color="text.secondary">Change the form or date filters to analyse available responses.</Typography>
    </Paper>}
    {selectedSubmissions.length > 0 && <>
      <Typography variant="body2" color="text.secondary">{responseTotal} question responses from the selected form. Questions are analysed independently, so different forms are never mixed.</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 1.5 }}>
        {questions.map((question) => <Paper key={question.key} variant="outlined" sx={{ p: 1.5, borderColor: '#e5ddd1' }}>
          <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }}>{question.label}</Typography>
            <Chip size="small" label={`${question.responseCount}/${selectedSubmissions.length}`} variant="outlined" />
          </Stack>
          <Stack spacing={0.9}>
            {question.answers.slice(0, 8).map((answer) => {
              const percent = Math.round((answer.count / question.responseCount) * 100);
              return <Box key={answer.value}>
                <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.35 }}>
                  <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{answer.value}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{answer.count} ({percent}%)</Typography>
                </Stack>
                <Box sx={{ height: 6, borderRadius: 3, bgcolor: '#f1ece4', overflow: 'hidden' }}><Box sx={{ width: `${percent}%`, height: '100%', bgcolor: GOLD, borderRadius: 3 }} /></Box>
              </Box>;
            })}
            {question.answers.length > 8 && <Typography variant="caption" color="text.secondary">+ {question.answers.length - 8} less common answers</Typography>}
          </Stack>
        </Paper>)}
      </Box>
    </>}
  </Stack>;
}

export default function MetaLeadsPage() {
  const db = useMemo(() => getFirestore(), []);
  const { currentUser, roleId } = useAuth();
  const [submissions, setSubmissions] = useState<MetaSubmission[]>([]);
  const [loadError, setLoadError] = useState('');
  const [filter, setFilter] = useState<'pending' | 'existing' | 'all' | 'converted'>('pending');
  const [creating, setCreating] = useState<MetaSubmission | null>(null);
  const [view, setView] = useState<'inbox' | 'insights'>('inbox');
  const [expandedId, setExpandedId] = useState('');
  const [ignoringId, setIgnoringId] = useState('');
  const [existingLeadsBySubmissionId, setExistingLeadsBySubmissionId] = useState<Record<string, ExistingLead>>({});
  const [matchingLeadsLoading, setMatchingLeadsLoading] = useState(false);
  const [linkedSubmission, setLinkedSubmission] = useState<MetaSubmission | null>(null);
  const [openExistingLead, setOpenExistingLead] = useState<ExistingLead | null>(null);
  const [leadDrawerWidth, setLeadDrawerWidth] = useState(460);
  const isAdmin = ['admin', 'super-admin', 'superadmin'].includes(String(roleId || '').toLowerCase());

  useEffect(() => {
    const q = query(collection(db, 'metaLeadSubmissions'), orderBy('receivedAt', 'desc'), limit(300));
    return onSnapshot(q, (snapshot) => {
      setSubmissions(snapshot.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<MetaSubmission, 'id'>) })));
      setLoadError('');
    }, (error) => setLoadError(error.message || 'Could not load Meta lead submissions.'));
  }, [db]);

  useEffect(() => {
    let active = true;
    const pending = submissions.filter((submission) => (
      isAfterMetaLeadsCutover(submission) &&
      (submission.status === 'pending' || submission.status === 'existing')
    ));
    const submissionIdsByPhone = new Map<string, string[]>();
    pending.forEach((submission) => {
      const normalized = phone10(submission.phone);
      if (!normalized) return;
      submissionIdsByPhone.set(normalized, [...(submissionIdsByPhone.get(normalized) || []), submission.id]);
    });

    if (!submissionIdsByPhone.size) {
      setExistingLeadsBySubmissionId({});
      setMatchingLeadsLoading(false);
      return () => { active = false; };
    }
    setMatchingLeadsLoading(true);

    (async () => {
      const matches = await Promise.all(Array.from(submissionIdsByPhone.keys()).map(async (normalized) => {
        const snapshot = await getDocs(query(collection(db, 'leads'), where('phone10', '==', normalized), limit(20)));
        const lead = snapshot.docs
          .map((item) => ({ id: item.id, ...(item.data() as Omit<ExistingLead, 'id'>) }))
          .filter((item) => !item.archived && !item.isMerged)
          .sort((a, b) => (asDate(b.updatedAt)?.getTime() || asDate(b.createdAt)?.getTime() || 0) - (asDate(a.updatedAt)?.getTime() || asDate(a.createdAt)?.getTime() || 0))[0];
        return { normalized, lead };
      }));
      if (!active) return;
      const next: Record<string, ExistingLead> = {};
      matches.forEach(({ normalized, lead }) => {
        if (!lead) return;
        (submissionIdsByPhone.get(normalized) || []).forEach((submissionId) => { next[submissionId] = lead; });
      });
      setExistingLeadsBySubmissionId(next);
      setMatchingLeadsLoading(false);
    })().catch((error) => {
      if (active) {
        console.error('Could not check existing CRM leads for Meta submissions.', error);
        setMatchingLeadsLoading(false);
      }
    });

    return () => { active = false; };
  }, [db, submissions]);

  const workflowSubmissions = submissions.filter(isAfterMetaLeadsCutover);
  const effectiveStatus = (submission: MetaSubmission): SubmissionStatus => {
    const storedStatus = submission.status || 'pending';
    if (storedStatus !== 'pending' && storedStatus !== 'existing') return storedStatus;
    const matchedLead = existingLeadsBySubmissionId[submission.id];
    if (!matchedLead) return storedStatus;

    const receivedAt = asDate(submission.receivedAt) || asDate(submission.metaCreatedAt);
    const closedAt = asDate(matchedLead.closedAt);
    const normalizedLeadStatus = String(matchedLead.status || '').trim().toLowerCase();
    const closedByStatus = ['closed', 'completed', 'cancelled'].includes(normalizedLeadStatus);
    const wasClosedBeforeSubmission = closedAt
      ? !receivedAt || receivedAt.getTime() >= closedAt.getTime()
      : closedByStatus;
    return wasClosedBeforeSubmission ? 'pending' : 'existing';
  };
  const visibleSubmissions = workflowSubmissions.filter((submission) => {
    if (filter === 'all') return true;
    if ((filter === 'pending' || filter === 'existing') && matchingLeadsLoading) return false;
    return effectiveStatus(submission) === filter;
  });
  const pendingCount = workflowSubmissions.filter((submission) => effectiveStatus(submission) === 'pending').length;
  const existingCount = workflowSubmissions.filter((submission) => effectiveStatus(submission) === 'existing').length;
  const linkedActivityNotes = useMemo(() => linkedSubmission ? notesFromAnswers(linkedSubmission) : [], [linkedSubmission]);

  const markConverted = async (leadId?: string) => {
    if (!creating || !leadId) return;
    const submission = creating;
    try {
      await updateDoc(doc(db, 'metaLeadSubmissions', submission.id), {
        status: 'converted',
        convertedLeadId: leadId,
        reviewedBy: currentUser?.email || null,
        reviewedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } finally {
      setCreating(null);
    }
  };

  const ignoreSubmission = async (submission: MetaSubmission) => {
    if (!isAdmin) return;
    setIgnoringId(submission.id);
    try {
      await updateDoc(doc(db, 'metaLeadSubmissions', submission.id), {
        status: 'ignored',
        updatedAt: serverTimestamp(),
      });
    } catch (error: any) {
      setLoadError(error?.message || 'Could not ignore the Meta lead submission.');
    } finally {
      setIgnoringId('');
    }
  };

  const openMatchedLead = (submission: MetaSubmission, lead: ExistingLead) => {
    // Keep the Meta form visible next to the CRM lead so the user can act on it.
    setExpandedId(submission.id);
    setLinkedSubmission(submission);
    setOpenExistingLead(lead);
  };

  const markExistingLeadActivityLogged = async (lead: ExistingLead) => {
    if (!linkedSubmission) return;
    try {
      await updateDoc(doc(db, 'metaLeadSubmissions', linkedSubmission.id), {
        status: 'converted',
        convertedLeadId: lead.id,
        linkedExistingLead: true,
        reviewedBy: currentUser?.email || null,
        reviewedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setLinkedSubmission(null);
    } catch (error: any) {
      setLoadError(error?.message || 'The activity was logged, but the Meta submission could not be linked.');
    }
  };

  return (
    <Box sx={{
      p: { xs: 1.5, md: 2 },
      maxWidth: openExistingLead ? 'none' : 1440,
      mx: openExistingLead ? 0 : 'auto',
      pr: { xs: 1.5, md: openExistingLead ? `${leadDrawerWidth + 16}px` : 2 },
      transition: 'padding-right 180ms ease',
    }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1.5 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <InboxOutlinedIcon sx={{ color: GOLD, fontSize: 22 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Meta Leads</Typography>
            {pendingCount > 0 && <Chip label={`${pendingCount} pending`} size="small" sx={{ bgcolor: '#f6eddf', color: GOLD, fontWeight: 700 }} />}
            {existingCount > 0 && <Chip label={`${existingCount} existing`} size="small" color="info" variant="outlined" />}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Review Meta form submissions from 20 Sep 2026 onward before creating CRM leads.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
          <ToggleButtonGroup value={view} exclusive size="small" onChange={(_, value) => value && setView(value)}>
            <ToggleButton value="inbox">Inbox</ToggleButton>
            <ToggleButton value="insights">Insights</ToggleButton>
          </ToggleButtonGroup>
          {view === 'inbox' && <ToggleButtonGroup value={filter} exclusive size="small" onChange={(_, value) => value && setFilter(value)}>
            <ToggleButton value="pending">Pending</ToggleButton>
            <ToggleButton value="existing">Existing</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="converted">Converted</ToggleButton>
          </ToggleButtonGroup>}
        </Stack>
      </Stack>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {!loadError && view === 'insights' && <MetaLeadInsights submissions={workflowSubmissions} />}
      {!loadError && view === 'inbox' && !matchingLeadsLoading && visibleSubmissions.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderColor: '#eadfce' }}>
          <InboxOutlinedIcon sx={{ fontSize: 40, color: '#b9a17e', mb: 1 }} />
          <Typography variant="h6">No {filter === 'all' ? '' : filter} Meta leads</Typography>
          <Typography color="text.secondary">New approved Meta form submissions will appear here automatically.</Typography>
        </Paper>
      )}

      {view === 'inbox' && visibleSubmissions.length > 0 && <TableContainer component={Paper} variant="outlined" sx={{ borderColor: '#e5ddd1' }}>
        <Table size="small" sx={{ minWidth: 900 }} aria-label="Meta lead submissions">
          <TableHead>
            <TableRow sx={{ bgcolor: '#faf8f4' }}>
              <TableCell sx={{ width: 42 }} />
              <TableCell>Lead</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Received</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleSubmissions.map((submission) => {
              const status = effectiveStatus(submission);
              const isExpanded = expandedId === submission.id;
              const visibleAnswers = Object.entries(submission.answers || {}).filter(([key]) => shouldShowInAccordion(key));
              const matchedLead = existingLeadsBySubmissionId[submission.id];
              const hasExistingLead = status === 'existing' || Boolean(matchedLead);
              return <React.Fragment key={submission.id}>
                <TableRow hover sx={{ '& > *': { borderBottom: isExpanded ? 0 : undefined } }}>
                  <TableCell padding="checkbox">
                    <Tooltip title={isExpanded ? 'Hide form answers' : 'Show form answers'}>
                      <IconButton size="small" onClick={() => setExpandedId(isExpanded ? '' : submission.id)}>
                        {isExpanded ? <ExpandLessOutlinedIcon fontSize="small" /> : <ExpandMoreOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, maxWidth: 230, overflowWrap: 'anywhere' }}>{submission.name || 'Unnamed Meta lead'}</TableCell>
                  <TableCell sx={{ maxWidth: 250, overflowWrap: 'anywhere' }}>
                    <Typography variant="body2">{submission.phone || '-'}</Typography>
                    {submission.email && <Typography variant="caption" color="text.secondary">{submission.email}</Typography>}
                  </TableCell>
                  <TableCell><Typography variant="body2" noWrap>{displayDate(submission)}</Typography></TableCell>
                  <TableCell><Chip size="small" label={submission.linkedExistingLead ? 'linked' : status === 'existing' ? 'existing lead' : status} color={status === 'pending' ? 'warning' : status === 'converted' ? 'success' : 'default'} /></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {(status === 'pending' || status === 'existing') ? <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                      <Tooltip title={hasExistingLead ? `Open existing lead${matchedLead ? `: ${matchedLead.name}` : ''}` : 'Create lead'}>
                        <IconButton size="small" aria-label={hasExistingLead ? 'Open existing lead' : 'Create lead'} onClick={() => matchedLead ? openMatchedLead(submission, matchedLead) : setCreating(submission)} sx={{ color: GOLD }}>
                          {hasExistingLead ? <OpenInNewOutlinedIcon /> : <AddTaskOutlinedIcon />}
                        </IconButton>
                      </Tooltip>
                      {(status === 'pending' || status === 'existing') && isAdmin && <Tooltip title="Ignore submission">
                        <IconButton size="small" aria-label="Ignore submission" disabled={ignoringId === submission.id} onClick={() => ignoreSubmission(submission)} color="inherit">
                          <DoNotDisturbAltOutlinedIcon />
                        </IconButton>
                      </Tooltip>}
                    </Stack> : status === 'converted' && submission.convertedLeadId ? <Chip size="small" label={submission.linkedExistingLead ? 'Activity logged' : 'Lead created'} variant="outlined" /> : '-' }
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 0, borderBottom: isExpanded ? undefined : 0 }}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 1.5, px: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Form answers</Typography>
                        <Divider sx={{ my: 0.75 }} />
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1 }}>
                          {visibleAnswers.map(([key, value]) => (
                            <Box key={key} sx={{ bgcolor: '#faf8f4', borderRadius: 1, px: 1, py: 0.75 }}>
                              <Typography variant="caption" color="text.secondary">{labelForAnswer(key)}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{Array.isArray(value) ? value.join(', ') : String(value || '-')}</Typography>
                            </Box>
                          ))}
                          {visibleAnswers.length === 0 && <Typography variant="body2" color="text.secondary">No additional form answers.</Typography>}
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>;
            })}
          </TableBody>
        </Table>
      </TableContainer>}

      <AddLeadDrawer
        open={Boolean(creating)}
        onClose={() => setCreating(null)}
        onCreated={markConverted}
        prefill={creating ? {
          name: creating.name || '',
          phone: creating.phone || '',
          source: 'Social Ad',
          lockedSource: 'Social Ad',
          notes: notesFromAnswers(creating),
          metaLeadQuickAdd: true,
        } : undefined}
      />
      <LeadDetailDrawer
        open={Boolean(openExistingLead)}
        lead={openExistingLead}
        closeOnOutsideClick
        onClose={() => {
          setOpenExistingLead(null);
          setLinkedSubmission(null);
        }}
        onWidthChange={setLeadDrawerWidth}
        initialActivityType="internal"
        initialActivityNotes={linkedActivityNotes}
        onActivityLogged={(lead) => { void markExistingLeadActivityLogged(lead as ExistingLead); }}
      />
    </Box>
  );
}
